import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Square, Eye, Sparkles, Share2, UserCheck } from 'lucide-react';
import { getPropertyCurrency, formatConvertedPrice, formatAreaSize } from '../utils/propertyPrice';
import { useCountry } from '../contexts/CountryContext';
import { EpcBadge, TenureBadge } from './PropertyBadges';
import PulseBadge from './PulseBadge';
import ListingBadges from './ListingBadges';

const SHARE_FEEDBACK_MS = 800;

const PropertyCard = ({ property, showAIBadge = true }) => {
  const { country, exchangeRates } = useCountry();
  const propertyCurrency = getPropertyCurrency(property);
  const formatPrice = (price) => formatConvertedPrice(price, propertyCurrency, country, exchangeRates);
  const isTestProperty = property.title?.startsWith('TEST') || property.title?.startsWith('Test Luxury Villa');

  // Type-aware fallback so a plot/land listing doesn't show an interior photo
  const FALLBACK_IMAGES = {
    land: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800&auto=format&fit=crop',
    commercial: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&auto=format&fit=crop',
    studio: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&auto=format&fit=crop',
    apartment: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&auto=format&fit=crop',
    penthouse: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&auto=format&fit=crop',
    cottage: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=800&auto=format&fit=crop',
    bungalow: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800&auto=format&fit=crop',
  };
  const defaultImage = FALLBACK_IMAGES[property.property_type] || 'https://images.unsplash.com/photo-1757262798620-c2cc40cfb440?w=800&auto=format&fit=crop';

  const shareProperty = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/property/${property.id}`;
    const text = `🏠 ${property.title} | ${property.city} | ${formatPrice(property.price)}${property.listing_type === 'rent' ? '/mo' : ''} | ${property.bedrooms}B ${property.bathrooms}Ba | ${url}`;
    try {
      // Fallback: create temporary textarea to copy
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      const el = e.currentTarget;
      el.style.background = '#E07A3A';
      el.style.color = '#fff';
      setTimeout(() => { el.style.background = ''; el.style.color = ''; }, SHARE_FEEDBACK_MS);
    } catch (err) {
      window.prompt('Copy this text:', text);
    }
  };

  return (
    <Link 
      to={`/property/${property.id}`}
      className="property-card-luxury block luxury-card overflow-hidden group"
      data-testid={`property-card-${property.id}`}
    >
      {/* Image Container */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={property.images?.[0] || property.panoramic_image || defaultImage}
          alt={property.title}
          className="property-image w-full h-full object-cover"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
          <span className={`px-4 py-1.5 text-xs tracking-widest uppercase ${
            property.listing_type === 'sale' 
              ? 'bg-[#E07A3A] text-white' 
              : 'bg-white/90 text-[#0F172A] backdrop-blur-sm'
          }`}>
            {property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}
          </span>
          {property.property_type === 'land' && (
            <span className="px-3 py-1.5 text-xs tracking-widest uppercase bg-emerald-600 text-white font-semibold" data-testid="badge-plot">
              Plot / Land
            </span>
          )}
          {property.property_type === 'commercial' && (
            <span className="px-3 py-1.5 text-xs tracking-widest uppercase bg-slate-700 text-white font-semibold" data-testid="badge-commercial">
              Commercial
            </span>
          )}
          {property.panoramic_image && (
            <span className="px-3 py-1.5 text-xs tracking-wider bg-white/90 text-[#0F172A] backdrop-blur-sm">
              360°
            </span>
          )}
        </div>

        {/* AI Valuation Badge */}
        {showAIBadge && property.ai_valuation && (
          <div className="absolute top-4 right-4">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E07A3A]/20 backdrop-blur-sm border border-[#E07A3A]/30">
              <span className="w-2 h-2 bg-[#E07A3A] animate-pulse" />
              <Sparkles className="w-3 h-3 text-[#E07A3A]" />
              <span className="text-xs text-white tracking-wider font-medium">AI VALUED</span>
            </div>
          </div>
        )}

        {/* Test Property Stamp */}
        {isTestProperty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="border-4 border-[#E07A3A]/40 px-6 py-2 rotate-[-18deg]" style={{ backgroundColor: 'rgba(224,122,58,0.12)', backdropFilter: 'blur(1px)' }}>
              <span className="text-[#E07A3A] text-sm font-bold tracking-[0.3em] uppercase" style={{ textShadow: '0 1px 2px rgba(255,255,255,0.8)' }}>
                SHOPPERDEN TEST
              </span>
            </div>
          </div>
        )}

        {/* Price Tag - bottom overlay */}
        <div className="absolute bottom-4 left-4">
          <span className="font-['Playfair_Display'] text-2xl font-medium text-white drop-shadow-lg">
            {formatPrice(property.price)}
            {property.listing_type === 'rent' && <span className="text-base font-normal text-white/70">/mo</span>}
          </span>
          {propertyCurrency && country && propertyCurrency.toLowerCase() !== country.toLowerCase().replace('uk','gbp').replace('in','inr').replace('ca','cad') && (
            <p className="text-[10px] text-white/80 tracking-wider uppercase drop-shadow" data-testid={`listed-currency-${property.id}`}>
              Listed in {propertyCurrency.toUpperCase()}
            </p>
          )}
        </div>

        {/* Views & Share */}
        <div className="absolute bottom-4 right-4 flex items-center gap-2">
          <button onClick={shareProperty}
            className="w-8 h-8 bg-white/80 backdrop-blur-sm flex items-center justify-center text-slate-600 hover:bg-white hover:text-[#E07A3A] transition-colors opacity-0 group-hover:opacity-100"
            data-testid={`share-card-${property.id}`}>
            <Share2 className="w-3.5 h-3.5" />
          </button>
          {property.views > 0 && (
            <div className="flex items-center gap-1 text-white/70 text-xs">
              <Eye className="w-3 h-3" />
              {property.views}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] mb-1 line-clamp-1 group-hover:text-[#E07A3A] transition-colors">
          {property.title}
        </h3>
        {property.ref_number && (
          <span className="text-[10px] tracking-[0.15em] text-[#E07A3A] font-bold uppercase mb-2 block" data-testid={`ref-${property.id}`}>{property.ref_number}</span>
        )}
        
        <div className="flex items-center gap-1 text-slate-400 text-sm mb-4">
          <MapPin className="w-4 h-4 flex-shrink-0" />
          <span className="line-clamp-1">{property.city || 'Location available'}</span>
        </div>

        {/* Agent Attribution Badge */}
        {property.listed_by_agent && property.agent_name && (
          <div className="flex items-center gap-1.5 mb-4 px-2.5 py-1.5 bg-[#FEF3EC] w-fit" data-testid={`agent-badge-${property.id}`}>
            <UserCheck className="w-3.5 h-3.5 text-[#E07A3A]" />
            <span className="text-xs font-semibold text-[#E07A3A] tracking-wide">Listed by {property.agent_name}</span>
            {property.agent_company && <span className="text-xs text-[#E07A3A]/60">({property.agent_company})</span>}
          </div>
        )}

        {/* Trust badges — Pulse-Verified + Google-Verified (Wave 2 Sales) */}
        <ListingBadges listingId={property.id} compact dataTestId={`trust-badges-${property.id}`} />

        {/* Tenure, EPC & Listing Type Badges */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {property.listing_type && (
            <span className={`px-2 py-0.5 text-[10px] tracking-wider uppercase font-bold border ${
              property.listing_type === 'sale' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'
            }`} data-testid={`listing-type-${property.id}`}>
              {property.listing_type === 'sale' ? 'For Sale' : 'To Let'}
            </span>
          )}
          {property.tenure && <TenureBadge tenure={property.tenure} />}
          {property.epc_rating && <EpcBadge rating={property.epc_rating} size="sm" />}
          <PulseBadge listingId={property.id} compact />
        </div>

        <div className="flex items-center gap-6 text-slate-300 text-sm pt-4 border-t border-slate-100">
          {property.property_type === 'land' ? (
            <>
              <div className="flex items-center gap-1.5">
                <Square className="w-4 h-4" />
                <span className="text-slate-600 font-medium">
                  {property.plot_area ? `${property.plot_area} ${property.plot_area_unit || 'sqft'}` : formatAreaSize(property.area_sqft, country)}
                </span>
              </div>
              {property.approval_authority && (
                <div className="flex items-center gap-1.5">
                  <UserCheck className="w-4 h-4 text-emerald-600" />
                  <span className="text-emerald-700 font-medium text-xs uppercase tracking-wider">{property.approval_authority}</span>
                </div>
              )}
              {property.facing && (
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-500 text-xs">↗ {property.facing} facing</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                <Bed className="w-4 h-4" />
                <span className="text-slate-500">{property.bedrooms}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Bath className="w-4 h-4" />
                <span className="text-slate-500">{property.bathrooms}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Square className="w-4 h-4" />
                <span className="text-slate-500">{formatAreaSize(property.area_sqft, country)}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
